import { useCallback } from 'react';
import type Handsontable from 'handsontable';
import { TRACKING_CLASS_USER, TRACKING_CLASS_DERIVED } from '../tracking';

interface UseCombinedCellsOptions {
  userCells?: Handsontable.GridSettings['cells'];
  cellsCallback: (row: number, col: number) => Handsontable.CellMeta | undefined;
  enableTracking: boolean;
  /** Настройки колонок — источник базового className (htCenter/htLeft/htRight) */
  columns?: Handsontable.ColumnSettings[];
}

const TRACKING_CLASSES = new Set<string>([TRACKING_CLASS_USER, TRACKING_CLASS_DERIVED]);

/**
 * Объединяет пользовательский cells callback с tracking callback.
 * Дедуплицирует className-токены из всех источников:
 * базовый класс колонки, пользовательский cells(), tracking.
 *
 * ВАЖНО: Handsontable мержит результат cells() в ПЕРСИСТЕНТНЫЙ cell meta
 * (DynamicCellMetaMod.extendCellMeta → updateCellMeta), поэтому базу нельзя
 * читать из this.className — туда возвращаются классы, добавленные нами же
 * на прошлом рендере. Это превращало подсветку в «храповик»: после сброса
 * или сохранения изменений класс tracking-cell--* было невозможно снять.
 * База берётся из настроек колонки, а className всегда возвращается строкой
 * (пусть и пустой), чтобы перезаписать устаревшее значение в meta.
 */
export function useCombinedCells({
  userCells,
  cellsCallback,
  enableTracking,
  columns,
}: UseCombinedCellsOptions): Handsontable.GridSettings['cells'] | undefined {
  const combinedCells = useCallback(function (
    this: Handsontable.CellProperties,
    row: number,
    col: number,
    prop: string | number,
  ): Handsontable.CellMeta {
    const userMeta = userCells?.call(this, row, col, prop) || {};
    const trackingMeta = cellsCallback(row, col);

    let baseClassName: string | string[] | undefined;
    if (Array.isArray(columns)) {
      baseClassName = columns[col]?.className;
    } else if (typeof this.className === 'string') {
      // Колонки не переданы — fallback на this.className, но с вычищенными
      // tracking-классами, иначе вернётся «храповик».
      baseClassName = this.className
        .split(/\s+/)
        .filter((token) => token && !TRACKING_CLASSES.has(token))
        .join(' ');
    }

    const userClassName = userMeta.className ?? '';
    const trackingClassName = trackingMeta?.className ?? '';

    const classSet = new Set(
      [baseClassName, userClassName, trackingClassName]
        .filter(Boolean)
        .flatMap((c) => (Array.isArray(c) ? c : (c as string).split(/\s+/)))
        .filter(Boolean),
    );
    const mergedClassName = Array.from(classSet).join(' ');

    return {
      ...userMeta,
      className: mergedClassName,
    };
  }, [userCells, cellsCallback, columns]);

  if (!enableTracking) return userCells;
  return combinedCells;
}
